import type { DueEntryT, ExtendedTrackerViewT } from "@patient-watch/types";
import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";
import { GenericChart, type ChartYAxisOptions } from "./components/GenericChart";
import { convertEntriesToChartConfig, convertEntriesToChartData } from "./components/utils";
import NoDataPlaceholder from "./NoDataPlaceholder";
import { ArrowBigDown, ArrowBigUp, Minus } from "lucide-react";
import { BMI_CATEGORY_BANDS, isBmiForm } from "./bmiChart";

type TrackerForChart = ExtendedTrackerViewT & {
    processed_schedule?: DueEntryT[];
};

type TrackerEntries = TrackerForChart["entries_view"];

/** Count questions, such as how many of the last 10 baseplates look like each picture. */
const COUNT_INPUT_KEY = "count";

/**
 * Splits a form's entries into the main series and the count series. Count questions go in their own
 * stacked bar chart, because their answers add up to a total. Without any charted counts, `counts` is null.
 */
export function splitCountSeries(entries: TrackerEntries): { main: TrackerEntries; counts: TrackerEntries | null } {
    const hasCharted = entries.some((entry) =>
        entry.entry_values?.some((value) => value.input_key === COUNT_INPUT_KEY && value.chart_order != null)
    );
    if (!hasCharted) {
        return { main: entries, counts: null };
    }

    const onlyValues = (keep: (key: string | null | undefined) => boolean) =>
        entries.map((entry) => ({
            ...entry,
            entry_values: entry.entry_values?.filter((value) => keep(value.input_key)),
        }));

    return {
        main: onlyValues((key) => key !== COUNT_INPUT_KEY),
        counts: onlyValues((key) => key === COUNT_INPUT_KEY),
    };
}

type ScoringDirection = "higher_better" | "lower_better" | "neutral";

const CHART_META_KEYS = new Set(["date", "timing", "timing_label"]);

function hasNumericChartPoints(tracker: TrackerForChart): boolean {
    const formId = tracker.form_id;
    if (!formId || !tracker.entries_view?.length) {
        return false;
    }

    const chartConfig = convertEntriesToChartConfig(tracker.entries_view, formId);
    if (Object.keys(chartConfig).length === 0) {
        return false;
    }

    const chartData = convertEntriesToChartData(tracker.entries_view, formId, tracker.processed_schedule);
    return chartData.some((point) =>
        Object.entries(point).some(
            ([key, value]) => !CHART_META_KEYS.has(key) && typeof value === "number" && !Number.isNaN(value)
        )
    );
}

/** Whether a tracker should render a chart (respects form.show_chart and chartable numeric data). */
export function shouldShowTrackerChart(tracker: TrackerForChart): boolean {
    if (tracker.form?.show_chart === false) {
        return false;
    }

    return hasNumericChartPoints(tracker);
}

function scoringHintCopy(locale: SupportedLocale) {
    if (locale === "de-DE") {
        return {
            higher: "Höhere Werte bedeuten bessere Ergebnisse",
            lower: "Niedrigere Werte bedeuten bessere Ergebnisse",
            neutral: "Werte werden an einem Zielbereich gemessen",
        };
    }
    if (locale === "fr-FR") {
        return {
            higher: "Des scores plus élevés indiquent de meilleurs résultats",
            lower: "Des scores plus faibles indiquent de meilleurs résultats",
            neutral: "Les scores sont mesurés par rapport à une plage cible",
        };
    }
    if (locale === "it-IT") {
        return {
            higher: "Punteggi più alti indicano risultati migliori",
            lower: "Punteggi più bassi indicano risultati migliori",
            neutral: "I punteggi sono misurati rispetto a un intervallo obiettivo",
        };
    }
    if (locale === "es-ES") {
        return {
            higher: "Las puntuaciones más altas indican mejores resultados",
            lower: "Las puntuaciones más bajas indican mejores resultados",
            neutral: "Las puntuaciones se miden respecto a un intervalo objetivo",
        };
    }
    if (locale === "da-DK") {
        return {
            higher: "Højere scorer betyder bedre resultater",
            lower: "Lavere scorer betyder bedre resultater",
            neutral: "Scorer måles i forhold til et målinterval",
        };
    }
    return {
        higher: "Higher scores indicate better outcomes",
        lower: "Lower scores indicate better outcomes",
        neutral: "Scores are measured against a target range",
    };
}

function BmiCategoryLegend() {
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-slate-700 text-2xs select-none">
            {BMI_CATEGORY_BANDS.map((band) => (
                <span key={band.label} className="inline-flex items-center gap-1.5">
                    <span
                        className="rounded-sm w-2.5 h-2.5 shrink-0"
                        style={{ backgroundColor: band.swatch ?? band.fill }}
                    />
                    {band.label}
                </span>
            ))}
        </div>
    );
}

function ScoringDirectionHint({ scoring, locale }: { scoring: ScoringDirection | null; locale: SupportedLocale }) {
    if (!scoring) return null;
    const copy = scoringHintCopy(locale);

    if (scoring === "neutral") {
        return (
            <div className="flex items-center gap-2 mt-1 text-slate-500 text-sm select-none">
                <Minus className="w-5 h-5 shrink-0" />
                <span>{copy.neutral}</span>
            </div>
        );
    }

    const isHigher = scoring === "higher_better";

    return (
        <div className="flex items-center gap-2 mt-1 text-slate-500 text-sm select-none">
            {isHigher ? <ArrowBigUp className="w-5 h-5 shrink-0" /> : <ArrowBigDown className="w-5 h-5 shrink-0" />}
            <span>{isHigher ? copy.higher : copy.lower}</span>
        </div>
    );
}

interface TrackerChartsProps {
    printMode?: boolean;
    tracker: TrackerForChart;
    locale?: SupportedLocale;
    /** Smaller chart height, hidden scoring hint - use for compact/sparkline-style placements. */
    compact?: boolean;
    /** Palette slot 1-5; series colours start here and wrap through --chart-1…--chart-5. */
    colorIndex?: number;
    /** Optional Y-axis domain / scale overrides (analytics builder). */
    yAxis?: ChartYAxisOptions;
}

export function TrackerChart({
    tracker,
    printMode = false,
    locale = DEFAULT_LOCALE,
    compact = false,
    colorIndex,
    yAxis,
}: TrackerChartsProps) {
    const formId = tracker.form_id;
    const hasEntries = tracker.entries_view?.length > 0;
    const scoring = (tracker.form?.scoring as ScoringDirection | null) ?? null;

    if (!formId || !shouldShowTrackerChart(tracker)) {
        return null;
    }

    if (!hasEntries) {
        return <NoDataPlaceholder locale={locale} />;
    }

    const chartType = (tracker.form?.chart_type ?? "line") as "line" | "stacked_area" | "bar" | "stacked_bar";
    const bmiChart = isBmiForm(formId);
    const { main, counts } = splitCountSeries(tracker.entries_view);
    const showMain = hasNumericChartPoints({ ...tracker, entries_view: main });

    return (
        <div className="my-2 space-y-6 w-full min-w-0">
            {showMain && (
                <>
                    <GenericChart
                        entries={main}
                        formId={formId}
                        processedSchedule={tracker.processed_schedule}
                        chartType={chartType}
                        printMode={printMode}
                        locale={locale}
                        compact={compact}
                        colorIndex={colorIndex}
                        yAxis={yAxis}
                        backgroundBands={bmiChart ? BMI_CATEGORY_BANDS : undefined}
                        fillLineArea={!bmiChart}
                    />
                    {!printMode &&
                        !compact &&
                        (bmiChart ? <BmiCategoryLegend /> : <ScoringDirectionHint scoring={scoring} locale={locale} />)}
                </>
            )}
            {counts && (
                <GenericChart
                    entries={counts}
                    formId={formId}
                    processedSchedule={tracker.processed_schedule}
                    chartType="stacked_bar"
                    printMode={printMode}
                    locale={locale}
                    compact={compact}
                    colorIndex={colorIndex}
                />
            )}
        </div>
    );
}

export default TrackerChart;
